import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";

const DIR_LABEL = {
    draft: "Brouillon IA",
    sent_to_artisan: "Envoyé à l'artisan",
    received_from_artisan: "Reçu de l'artisan",
    note: "Note interne",
};

export default function MessagesDialog({ devis, refreshKey }) {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState("");
    const [direction, setDirection] = useState("note");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const data = await api.listMessages(devis.id);
            setMessages(data);
        } catch (e) {
            console.error("MessagesDialog load failed:", e);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line
    }, [open, refreshKey]);

    const add = async () => {
        if (!content.trim()) return;
        setLoading(true);
        try {
            await api.addMessage(devis.id, { direction, content });
            setContent("");
            load();
        } catch {
            toast.error("Erreur d'ajout");
        } finally {
            setLoading(false);
        }
    };

    const del = async (id) => {
        await api.deleteMessage(id);
        load();
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    data-testid={`messages-btn-${devis.id}`}
                    variant="outline"
                    className="rounded-none border-ink"
                >
                    <MessageCircle
                        className="w-4 h-4 mr-2"
                        strokeWidth={1.6}
                    />
                    Échanges ({messages.length || 0})
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        Échanges — {devis.contractor_name}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[40vh] overflow-y-auto">
                    {messages.length === 0 ? (
                        <div className="text-sm text-ink-soft text-center py-6">
                            Aucun échange pour le moment.
                        </div>
                    ) : (
                        messages.map((m) => (
                            <div
                                key={m.id}
                                className="border border-border bg-white p-3"
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="pill border-ink/30 bg-[#FAFAFA] text-ink-soft">
                                        {DIR_LABEL[m.direction] || m.direction}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-ink-soft">
                                            {fmtDate(m.created_at)}
                                        </span>
                                        <button
                                            onClick={() => del(m.id)}
                                            className="text-ink-soft hover:text-danger"
                                        >
                                            <Trash2
                                                className="w-3.5 h-3.5"
                                                strokeWidth={1.5}
                                            />
                                        </button>
                                    </div>
                                </div>
                                {m.subject && (
                                    <div className="text-xs font-semibold mb-1">
                                        {m.subject}
                                    </div>
                                )}
                                <pre className="text-sm font-sans whitespace-pre-wrap leading-relaxed">
                                    {m.content}
                                </pre>
                            </div>
                        ))
                    )}
                </div>

                <div className="space-y-2 border-t border-border pt-3">
                    <div className="flex items-center gap-2">
                        <Select value={direction} onValueChange={setDirection}>
                            <SelectTrigger className="rounded-none w-[220px] h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="note">
                                    Note interne
                                </SelectItem>
                                <SelectItem value="sent_to_artisan">
                                    Envoyé à l'artisan
                                </SelectItem>
                                <SelectItem value="received_from_artisan">
                                    Reçu de l'artisan
                                </SelectItem>
                                <SelectItem value="draft">
                                    Brouillon
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Textarea
                        rows={3}
                        className="rounded-none"
                        placeholder="Ajouter un échange ou une note…"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        data-testid="message-input"
                    />
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                    >
                        Fermer
                    </Button>
                    <Button
                        className="rounded-none bg-ink text-white hover:bg-ink/90"
                        onClick={add}
                        disabled={loading || !content.trim()}
                        data-testid="message-add"
                    >
                        Ajouter
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
